
/**
 * Direct valuation service
 * Updated: 2025-05-01 - Fixed request format and error handling
 * Updated: 2025-11-08 - Added Turnstile token support and Polish error messages
 * Updated: 2025-11-17 - Fixed to trust backend's calculated prices instead of recalculating
 * Updated: 2026-01-20 - Fixed manual valuation response handling to prevent error toast
 */

import { calculateReservePrice } from '@/utils/valuation/valuationCalculator';
import { supabase } from "@/integrations/supabase/client";
import md5 from 'crypto-js/md5';
import { toast } from 'sonner';

// Define valuation method for tracking purposes
export type ValuationMethod = 'direct-api' | 'edge-function' | 'fallback';

/**
 * Make a direct API call to the valuation service through our edge function
 * This bypasses CORS restrictions and handles API interaction securely
 */
export async function getDirectValuation(
  vin: string,
  mileage: number,
  gearbox: string = 'manual',
  turnstileToken: string | null = null
): Promise<{
  success: boolean;
  data?: any;
  error?: string;
  method: ValuationMethod;
  rawData?: any;
  requiresManualValuation?: boolean;
  code?: string;
}> {
  console.log('[DirectValuation] Starting direct API valuation for:', { 
    vin, 
    mileage: typeof mileage === 'number' ? mileage : Number(mileage), 
    gearbox,
    hasTurnstileToken: !!turnstileToken
  });
  
  // Ensure proper data types
  const validVin = String(vin).trim();
  const numericMileage = typeof mileage === 'number' ? mileage : Number(mileage);
  
  if (!validVin) {
    return {
      success: false,
      error: 'VIN is required',
      method: 'direct-api'
    };
  }
  
  if (isNaN(numericMileage) || numericMileage < 0) {
    return {
      success: false,
      error: 'Mileage must be a positive number',
      method: 'direct-api'
    };
  }
  
  try {
    // Use the Supabase edge function which will handle CORS properly
    console.log('[DirectValuation] Calling valuation via Edge Function proxy');
    
    const { data, error } = await supabase.functions.invoke('get-vehicle-valuation', {
      body: {
        vin: validVin,
        mileage: numericMileage,
        gearbox: gearbox || 'manual',
        turnstileToken,
        debug: true
      }
    });
    
    // Check for successful response
    if (error) {
      console.error('[DirectValuation] Edge function error:', error);
      
      // Handle Turnstile verification failure specifically
      if (error.status === 403 || error.message?.includes('verification')) {
        return {
          success: false,
          error: 'Weryfikacja bezpieczeństwa nie powiodła się. Odśwież stronę i spróbuj ponownie.',
          method: 'direct-api'
        };
      }
      
      return {
        success: false,
        error: 'Nie udało się uzyskać wyceny pojazdu',
        method: 'direct-api'
      };
    }
    
    // Check if response indicates manual valuation is required (no price data available)
    if (data?.requiresManualValuation === true) {
      console.log('[DirectValuation] Manual valuation required - no price data available');
      return {
        success: false,
        requiresManualValuation: true,
        code: data.code || 'NO_DATA_AVAILABLE',
        error: data.error || 'Brak danych wyceny dla tego pojazdu',
        method: 'direct-api'
      };
    }
    
    // Parse API response - only fail if no data AND not a manual valuation case
    if (!data || !data.data) {
      return {
        success: false,
        error: 'No data received from API',
        method: 'direct-api'
      };
    }
    
    const rawData = data.data;
    console.log('[DirectValuation] Raw API response received:', {
      dataSize: JSON.stringify(rawData).length,
      keys: Object.keys(rawData)
    });
    
    // Extract and normalize data
    const functionResponse = rawData.functionResponse || {};
    const userParams = functionResponse.userParams || {};
    const calcValuation = functionResponse.valuation?.calcValuation || {};
    
    // Extract essential fields with fallbacks
    const make = userParams.make || rawData.make || '';
    const model = userParams.model || rawData.model || '';
    const year = userParams.year || rawData.productionYear || rawData.year || 0;
    const engineCapacity = rawData.engineCapacity || userParams.capacity || '';
    
    // Extract pricing data with multiple fallback paths
    let priceMin = 0;
    let priceMed = 0;
    
    if (calcValuation.price_min !== undefined) {
      priceMin = Number(calcValuation.price_min);
    } else if (rawData.price_min !== undefined) {
      priceMin = Number(rawData.price_min);
    }
    
    if (calcValuation.price_med !== undefined) {
      priceMed = Number(calcValuation.price_med);
    } else if (rawData.price_med !== undefined) {
      priceMed = Number(rawData.price_med);
    }
    
    // Use backend's calculated values if available (preferred)
    // Only recalculate as fallback for old API responses
    let basePrice = 0;
    let reservePrice = 0;

    if (rawData.basePrice && rawData.reservePrice) {
      // Backend has already calculated these correctly
      basePrice = Number(rawData.basePrice);
      reservePrice = Number(rawData.reservePrice);
      console.log('[DirectValuation] Using backend calculated prices:', { basePrice, reservePrice });
    } else {
      // Fallback: calculate manually (for old cached responses)
      if (priceMin > 0 && priceMed > 0) {
        basePrice = (priceMin + priceMed) / 2;
      } else {
        basePrice = calcValuation.price || rawData.price || rawData.valuation || 0;
      }
      reservePrice = calculateReservePrice(basePrice);
      console.log('[DirectValuation] Calculated prices manually (fallback):', { basePrice, reservePrice });
    }
    
    console.log('[DirectValuation] Processed valuation data:', {
      make,
      model,
      year,
      engineCapacity,
      basePrice,
      reservePrice
    });
    
    // Return normalized data matching expected structure
    return {
      success: true,
      data: {
        vin,
        make,
        model,
        year,
        mileage,
        transmission: gearbox,
        engineCapacity,
        basePrice,
        reservePrice,
        valuation: basePrice,
        averagePrice: priceMed || basePrice,
        price_min: priceMin,
        price_med: priceMed,
        functionResponse  // Include the complete function response for compatibility
      },
      method: 'direct-api',
      rawData // Include raw data for diagnostic purposes
    };
  } catch (error: any) {
    console.error('[DirectValuation] Error:', error);
    
    // Provide detailed error information
    return {
      success: false,
      error: `Direct valuation error: ${error.message || error}`,
      method: 'direct-api'
    };
  }
}

/**
 * Enhanced validation function for direct VIN validation
 */
export async function validateVinDirectly(vin: string, mileage: number = 0): Promise<any> {
  try {
    const result = await getDirectValuation(vin, mileage);
    return result.data;
  } catch (error: any) {
    console.error('[DirectValuation] Validation error:', error);
    throw new Error(error.message || 'VIN validation failed');
  }
}
